import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import dryftService from '../services/dryftService';

// Configuration Sets tab — reusable named subsets of OHIP config sub-areas. Rendered inside
// DryftHomePage. A project references a set (by id) to decide what config to watch.

const DryftConfigSetList = () => {
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth();
    const { selectedAccount, selectedPartner } = useAccount();
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await dryftService.listConfigSets({
                account_id: selectedAccount || undefined,
                partner_id: selectedPartner || undefined,
            });
            setItems(res.data?.configSets || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load configuration sets');
        } finally {
            setLoading(false);
        }
    }, [selectedAccount, selectedPartner]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Delete this configuration set?')) return;
        try {
            await dryftService.removeConfigSet(id);
            setItems(prev => prev.filter(c => c.id !== id));
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete configuration set');
        }
    };

    const filtered = items.filter(c =>
        !search ||
        (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.description || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <>
            <div className="pi-toolbar-row">
                <div className="pi-search-wrap">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                        <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                        <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <input
                        type="text"
                        className="pi-search-input"
                        placeholder="Search configuration sets..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <button className="pi-btn-add" onClick={() => navigate('/apps/dryft/config-sets/new')}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    New Set
                </button>
            </div>

            {error && <div className="ppw-error-msg" style={{ margin: '8px 0' }}>{error}</div>}

            <div className="pi-list-panel">
                {loading ? (
                    <div className="pi-empty"><p className="pi-empty-text">Loading…</p></div>
                ) : filtered.length === 0 ? (
                    <div className="pi-empty">
                        <p className="pi-empty-title">No configuration sets yet</p>
                        <p className="pi-empty-text">A configuration set is a named bundle of OHIP config areas (e.g. "Rates" = Rate Codes + Packages) that a project can watch.</p>
                        <button className="pi-btn-add" onClick={() => navigate('/apps/dryft/config-sets/new')}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            New Set
                        </button>
                    </div>
                ) : (
                    <div className="pi-list">
                        {filtered.map(c => {
                            const open = () => navigate(`/apps/dryft/config-sets/${c.id}`);
                            const count = c.subarea_count ?? 0;
                            return (
                                <div
                                    key={c.id}
                                    className="pi-list-item"
                                    onClick={open}
                                    onKeyDown={e => e.key === 'Enter' && open()}
                                    role="button"
                                    tabIndex={0}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="pi-item-main">
                                        <div className="pi-item-name">{c.name}</div>
                                        <div className="pi-item-sub">
                                            {count} config area{count === 1 ? '' : 's'}
                                            {c.description ? ` · ${c.description}` : ''}
                                            {c.active ? '' : ' · Inactive'}
                                        </div>
                                    </div>
                                    {isSuperAdmin && (
                                        <button className="occ-link-btn" onClick={(e) => handleDelete(e, c.id)}>Delete</button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

export default DryftConfigSetList;
